import * as XLSX from "xlsx";
import { enrollmentTerminal } from "../services/terminal/enrollmentTerminalService";
import { createUserInKeycloak, checkUserExists } from "../services/userService";
import { ensureTerminalIds } from "./terminalId";

const parseExcelDate = (excelSerialDate) => {
  if (!isNaN(excelSerialDate) && excelSerialDate > 10000) {
    const date = XLSX.SSF.parse_date_code(excelSerialDate);

    if (date && date.y && date.m && date.d) {
      return `${date.y}-${String(date.m).padStart(2, "0")}-${String(
        date.d
      ).padStart(2, "0")}`;
    }
  }
  return excelSerialDate;
};

export const processFile = (file, acquirerData) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    const fieldLengthLimits = {
      Email: 50,
      "Merchant Name": 100,
      Store: 8,
      Address: 200,
      City: 50,
      State: 32,
      "Postal Code": 20,
      mccID: 4,
      MerchantID: 20,
      "Currency Code": 3,
      taxpayerID: 20,
      legalName: 16,
      businessType: 50,
      businessPhone: 16,
      businessEmail: 50,
      amountTxProcessing: 5,
      percentTxProcessing: 7,
      firstName: 50,
      lastName: 50,
      ID: 20,
      address: 200,
      city: 50,
      county: 2,
      state: 2,
      postalCode: 20,
      personalPhone: 16,
      personalEmail: 50,
      accountNumber: 18,
      routingNumber: 12,
      terminalID: 64,
      terminalType: 20,
      terminalSerial: 30,
    };

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const cellData = XLSX.utils.sheet_to_json(sheet, {
          header: 1,
          blankrows: true,
          defval: "",
        });

        const filteredCellData = cellData.filter(
          (row) => !row.every((cell) => cell === "")
        );
        const headers = filteredCellData[1];

        filteredCellData.shift();
        filteredCellData.shift();

        const dateFields = ["incorporationDate", "effectiveDate"];

        const jsonData = [];

        for (let rowIndex = 0; rowIndex < filteredCellData.length; rowIndex++) {
          const row = filteredCellData[rowIndex];
          const rowData = {};
          headers.forEach((header, index) => {
            let value = row[index];
            if (dateFields.includes(header)) {
              value = parseExcelDate(value);
            }
            rowData[header] = value || null;
          });

          if (
            acquirerData?.isoID &&
            (rowData["Iso"] === "False" || rowData["Iso"] === null)
          ) {
            return reject({
              message: `Field "Iso" must be "True" when isoID exists.`,
              data: { ...rowData, lineNumber: rowIndex + 2 },
            });
          }

          const requiredFields = [
            "Email",
            "Merchant Name",
            "Address",
            "City",
            "State",
            "Postal Code",
            "mccID",
            "MerchantID",
            "Currency Code",
          ];

          const missingFields = requiredFields.filter(
            (field) => !rowData[field]
          );
          if (missingFields.length > 0) {
            return reject({
              message: `The following required fields are missing: ${missingFields.join(
                ", "
              )}`,
              data: { ...rowData, lineNumber: rowIndex + 2 },
            });
          }

          if (rowData["Postal Code"] && isNaN(Number(rowData["Postal Code"]))) {
            return reject({
              message: `Postcode must be numeric.`,
              data: { ...rowData, lineNumber: rowIndex + 2 },
            });
          }

          const terminalData = [];
          const terminalKeys = Object.keys(rowData).filter(
            (key) =>
              key.startsWith("terminalID.") ||
              key.startsWith("terminalType.") ||
              key.startsWith("terminalSerial.")
          );

          const terminalGroups = {};
          terminalKeys.forEach((key) => {
            const [type, index] = key.split(".");
            if (!terminalGroups[index]) {
              terminalGroups[index] = {};
            }
            terminalGroups[index][type] = rowData[key];
          });

          const terminalsFromRow = Object.values(terminalGroups).map((group) => ({
            terminalID: group.terminalID || "",
            terminalType: group.terminalType || "",
            terminalSerial: group.terminalSerial || "",
          }));

          ensureTerminalIds(terminalsFromRow).forEach((terminal) => {
            const { terminalID, terminalType, terminalSerial } = terminal;
            const hasRequiredFields =
              terminalID && terminalType && terminalSerial;

            if (hasRequiredFields) {
              terminalData.push({
                terminalID,
                terminalType,
                terminalSerial,
              });
            }
          });

          const ecommerceTerminals = terminalData.filter(
            (terminal) => terminal.terminalType === "E"
          );
          if (ecommerceTerminals.length > 1) {
            return reject({
              message: `Only one terminal with type 'E' (E-commerce) is allowed per merchant.`,
              data: { ...rowData, lineNumber: rowIndex + 2 },
            });
          }

          for (const field in fieldLengthLimits) {
            const maxLength = fieldLengthLimits[field];

            if (field.startsWith("terminal")) {
              const matchingKeys = Object.keys(rowData).filter((k) =>
                k.startsWith(field)
              );
              for (const key of matchingKeys) {
                const value = rowData[key];
                if (value && value.length > maxLength) {
                  return reject({
                    message: `Field "${key}" exceeds max length of ${maxLength} characters.`,
                    data: { ...rowData, lineNumber: rowIndex + 2 },
                  });
                }
              }
            } else {
              const value = rowData[field];
              if (value && value.length > maxLength) {
                return reject({
                  message: `Field "${field}" exceeds max length of ${maxLength} characters.`,
                  data: { ...rowData, lineNumber: rowIndex + 2 },
                });
              }
            }
          }

          jsonData.push({
            acquirer: {
              iso: rowData["Iso"],
              isoID: rowData["IsoID"],
              email: rowData["Email"],
              merchantName: rowData["Merchant Name"],
              merchantStore: rowData["Store"],
              merchantAddress: rowData["Address"],
              merchantCity: rowData["City"],
              merchantState: rowData["State"],
              merchantPostalCode: rowData["Postal Code"],
              mccID: rowData["mccID"],
              merchantID: rowData["MerchantID"],
              terminal: {
                terminals: terminalData,
              },
            },
            merchant: {
              taxpayerID: rowData["taxpayerID"],
              legalName: rowData["legalName"],
              businessType: rowData["businessType"],
              businessPhone: rowData["businessPhone"],
              businessEmail: rowData["businessEmail"],
              amountTxProcessing: rowData["amountTxProcessing"],
              // ...(rowData["percentTxProcessing"] && {
              //   percentTxProcessing: rowData["percentTxProcessing"],
              // }),
              percentTxProcessing: rowData["percentTxProcessing"] || "0",
            },
            owner: [
              {
                firstName: rowData["firstName"],
                lastName: rowData["lastName"],
                taxpayerID: rowData["taxpayerID"],
                id: rowData["ID"],
                address: rowData["address"],
                city: rowData["city"],
                county: rowData["county"],
                state: rowData["state"],
                postalCode: rowData["postalCode"],
                personalPhone: rowData["personalPhone"],
                personalEmail: rowData["personalEmail"],
              },
            ],
            bank: {
              accountNumber: rowData["accountNumber"],
              routingNumber: rowData["routingNumber"],
              address: rowData["address"],
            },
            fiat: {
              currencyCode: rowData["Currency Code"],
            },
          });
        }

        resolve({ filteredCellData: jsonData });
      } catch (error) {
        console.error("Error reading file:", error);
        reject(error);
      }
    };

    reader.onerror = (err) => {
      console.error("File reading error:", err);
      reject(err);
    };

    reader.readAsArrayBuffer(file);
  });
};

export const sendData = async (data, url, token, acquirerData) => {
  const successfulData = [];
  const failedData = [];

  for (const item of data) {
    try {
      const email = item.acquirer.email;
      const sanitizedUsername = item.acquirer.merchantName
        .toLowerCase()
        .replace(/\s+/g, "_")
        .replace(/[^a-z0-9_]/g, "");

      const existingUser = await checkUserExists({
        username: sanitizedUsername,
        email,
        token,
        acquirerData,
      });

      if (existingUser) {
        failedData.push({
          item,
          message: "User already exists with this email or Merchant Name!",
        });
        continue;
      }
      const response = await enrollmentTerminal(url, item, token, acquirerData);

      if (response.data) {
        const sanitizedUsername = item.acquirer.merchantName
          .toLowerCase()
          .replace(/\s+/g, "_")
          .replace(/[^a-z0-9_]/g, "");

        const rol_data =
          item.acquirer.iso === true || item.acquirer.iso === "True"
            ? "ISO"
            : "merchant";

        const userData = {
          username: sanitizedUsername,
          email: item.acquirer.email,
          emailVerified: false,
          enabled: true,
          credentials: [
            {
              type: "password",
              value: "123456",
              temporary: true,
            },
          ],
          attributes: {
            "acquirer-rol-data": rol_data,
            "acquirer-id-data": acquirerData.acquirerId,
            "acquirer-name-data": acquirerData.acquirerName,
            "acquirer-currency-data": item.fiat.currencyCode,
            ...(rol_data === "ISO"
              ? { "acquirer-id-iso-data": item.acquirer.isoID }
              : {}),
          },
        };

        // try {
        //   await createUserInKeycloak(userData, token, acquirerData);
        successfulData.push({ item, response });
        // } catch (keycloakError) {
        //   console.error("Error creating user in Keycloak:", keycloakError);
        //   failedData.push({ item, error: keycloakError });
        // }
      }
    } catch (error) {
      console.error("sendData > Error sending data:", error);
      const {
        response: {
          status,
          data: { detail },
        },
      } = error;
      failedData.push({ item, error: { status, message: detail } });
    }
  }

  return { successfulData, failedData };
};
